"use client";

// El carrito de la tienda: una línea guardada en el navegador. Solo lectura.
import { useSyncExternalStore } from "react";
import { checkoutHref } from "@/dropai/checkout-link";
import type { ProductVariant } from "@/dropai/types";

export interface CartLine {
  options: Record<string, string>;
  units: number;
}

export interface CartState {
  line: CartLine | null;
  open: boolean;
  /** Sube en cada añadido: el contador salta y el carrito celebra (7.5). */
  added: number;
}

const KEY = "dropai-carrito";
const EMPTY: CartState = { line: null, open: false, added: 0 };
let state: CartState = EMPTY;
let loaded = false;
const listeners = new Set<() => void>();

function load() {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  try {
    const raw = window.localStorage.getItem(KEY);
    const line = raw ? (JSON.parse(raw) as CartLine) : null;
    if (line && typeof line.units === "number" && line.units > 0) state = { ...state, line };
  } catch {
    // Sin almacenamiento (modo privado): el carrito vive solo en esta visita.
  }
}

function set(next: Partial<CartState>) {
  state = { ...state, ...next };
  try {
    if (state.line) window.localStorage.setItem(KEY, JSON.stringify(state.line));
    else window.localStorage.removeItem(KEY);
  } catch {
    // Igual que arriba.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  load();
  listeners.add(listener);
  listener();
  return () => listeners.delete(listener);
}

export function useCart(): CartState {
  return useSyncExternalStore(subscribe, () => state, () => EMPTY);
}

export function addToCart(line: CartLine) {
  load();
  set({ line, open: true, added: state.added + 1 });
}

export function setUnits(units: number) {
  if (!state.line) return;
  set({ line: units < 1 ? null : { ...state.line, units: Math.min(units, 9) } });
}

export function removeLine() {
  set({ line: null });
}

export function openCart() {
  load();
  set({ open: true });
}

export function closeCart() {
  set({ open: false });
}

/** El enlace al pago con lo elegido: /checkout?talla=m&color=negro&unidades=2. */
export function payHref(variants: readonly ProductVariant[], line: CartLine): string {
  const base = checkoutHref(variants, line.options);
  return line.units > 1 ? base + (base.includes("?") ? "&" : "?") + "unidades=" + String(line.units) : base;
}
